/**
 * VerkaufsTimingOptimizer — Verkauf-Tab im FahrzeugDetail
 *
 * Zeigt:
 *   1. AfA-Verlauf-Diagramm (Recharts LineChart) mit Restbuchwert-Linie,
 *      Marktwert-Linie und Schnittpunkt-Marker
 *   2. Empfehlungs-Card mit 3 Szenarien (jetzt / optimal / Vollabschreibung)
 *   3. Steuer-Hinweis-Banner (Banner A + Banner B)
 *   4. Manueller Marktwert-Eintrag
 *
 * Issue #580
 */

import { useCallback, useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';
import { AlertTriangle, TrendingDown, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  berechneRestbuchwert,
  berechneAfaVerlauf,
  berechneSzenarien,
  berechneBannerZustand,
  berechneVollabschreibungsDatum,
  type AfaInput,
  type MarktwertPunkt,
  type AfaVerlaufPunkt,
} from '@/lib/verkaufs-timing-optimizer';
import { formatEuro, formatDateDE } from '@/lib/finanzen-tab';
import { localIsoDate } from '@/lib/date-utils';

// ── Typen ────────────────────────────────────────────────────────────────────

interface FahrzeugAfaDaten {
  kaufpreis_netto: number | null;
  afa_beginn: string | null;
  afa_nutzungsdauer_jahre: number | null;
}

interface MarktwertSchaetzung {
  id: string;
  geschaetzt_am: string;
  marktwert_brutto: number;
  quelle: string | null;
  notizen: string | null;
}

interface Props {
  fahrzeugId: string;
}

// ── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatMonthDE(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function heute(): string {
  return localIsoDate(new Date());
}

function buchgewinnText(buchgewinn: number | null): string {
  if (buchgewinn === null) return '— (kein Marktwert)';
  if (buchgewinn < 0) return `${formatEuro(Math.abs(buchgewinn))} Buchverlust (steuerlich günstig)`;
  if (buchgewinn === 0) return '0,00 € (neutral)';
  return `${formatEuro(buchgewinn)} steuerpflichtiger Buchgewinn`;
}

// ── Diagramm-Datenpunkte aufbereiten ─────────────────────────────────────────

function buildChartData(
  afaVerlauf: AfaVerlaufPunkt[],
  marktwertPunkte: MarktwertPunkt[],
): Array<{ datum: string; label: string; restbuchwert: number; marktwert?: number }> {
  const marktwertMap = new Map(marktwertPunkte.map(p => [p.datum, p.marktwert]));

  return afaVerlauf.map(p => ({
    datum: p.datum,
    label: formatMonthDE(p.datum),
    restbuchwert: Math.round(p.restbuchwert),
    marktwert: marktwertMap.has(p.datum) ? Math.round(marktwertMap.get(p.datum)!) : undefined,
  }));
}

// ── Unterkomponenten ─────────────────────────────────────────────────────────

function SzenarioCard({
  title,
  datum,
  restbuchwert,
  marktwert,
  buchgewinn,
  highlighted,
  warning,
}: {
  title: string;
  datum: string;
  restbuchwert: number;
  marktwert: number | null;
  buchgewinn: number | null;
  highlighted?: boolean;
  warning?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 space-y-2 ${
        warning
          ? 'border-red-200 bg-red-50'
          : highlighted
          ? 'border-green-200 bg-green-50'
          : 'border-gray-100 bg-white'
      }`}
      style={!warning && !highlighted ? { boxShadow: '0 1px 3px rgba(0,0,0,0.04)' } : undefined}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{title}</div>
      <div className="text-sm font-bold text-gray-900">{formatDateDE(datum)}</div>
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Restbuchwert</span>
          <span className="font-semibold text-gray-800">{formatEuro(restbuchwert)}</span>
        </div>
        <div className="flex justify-between">
          <span>Marktwert</span>
          <span className="font-semibold text-gray-800">
            {marktwert !== null ? formatEuro(marktwert) : '—'}
          </span>
        </div>
        <div className="pt-1 border-t border-gray-100">
          <div className="text-[11px] font-medium text-gray-700">
            → {buchgewinnText(buchgewinn)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────

export default function VerkaufsTimingOptimizer({ fahrzeugId }: Props) {
  const [afaDaten, setAfaDaten] = useState<FahrzeugAfaDaten | null>(null);
  const [marktwerte, setMarktwerte] = useState<MarktwertSchaetzung[]>([]);
  const [loading, setLoading] = useState(true);
  const [marktwertInput, setMarktwertInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: fzData }, { data: mwData }] = await Promise.all([
      supabase
        .from('fahrzeuge')
        .select('kaufpreis_netto, afa_beginn, afa_nutzungsdauer_jahre')
        .eq('id', fahrzeugId)
        .maybeSingle(),
      supabase
        .from('fahrzeug_marktwert_schaetzungen')
        .select('id, geschaetzt_am, marktwert_brutto, quelle, notizen')
        .eq('fahrzeug_id', fahrzeugId)
        .order('geschaetzt_am', { ascending: false })
        .limit(12),
    ]);
    if (fzData) setAfaDaten(fzData as FahrzeugAfaDaten);
    if (mwData) setMarktwerte(mwData as MarktwertSchaetzung[]);
    setLoading(false);
  }, [fahrzeugId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveMarktwert = async () => {
    const wert = parseFloat(marktwertInput.replace(',', '.'));
    if (isNaN(wert) || wert <= 0) {
      toast.error('Bitte einen gültigen Marktwert eingeben (z.B. 25000)');
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('fahrzeug_marktwert_schaetzungen')
      .insert({
        fahrzeug_id: fahrzeugId,
        geschaetzt_am: heute(),
        marktwert_brutto: wert,
        quelle: 'manuell',
      });
    setSaving(false);
    if (error) {
      toast.error('Fehler beim Speichern des Marktwerts');
      return;
    }
    toast.success('Marktwert gespeichert');
    setMarktwertInput('');
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Daten werden geladen...</span>
      </div>
    );
  }

  // Wenn AfA-Daten fehlen, Hinweis anzeigen
  if (
    !afaDaten ||
    !afaDaten.kaufpreis_netto ||
    !afaDaten.afa_beginn ||
    !afaDaten.afa_nutzungsdauer_jahre
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <TrendingDown className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-500">Keine AfA-Daten vorhanden</p>
        <p className="text-xs text-gray-400 mt-1">
          Bitte Kaufpreis netto, AfA-Beginn und Nutzungsdauer im Finanzen-Tab erfassen.
        </p>
      </div>
    );
  }

  const afaInput: AfaInput = {
    kaufpreis_netto: afaDaten.kaufpreis_netto,
    afa_beginn: afaDaten.afa_beginn,
    afa_nutzungsdauer_jahre: afaDaten.afa_nutzungsdauer_jahre,
  };

  const heuteDatum = heute();
  const vollabschreibungsDatum = berechneVollabschreibungsDatum(afaInput);
  const afaVerlauf = berechneAfaVerlauf(afaInput);

  // Neuester Marktwert für heutige Berechnungen
  const neusterMarktwert = marktwerte.length > 0
    ? marktwerte[0].marktwert_brutto
    : null;

  // Marktwert-Punkte für Diagramm und Schnittpunkt
  const marktwertPunkte: MarktwertPunkt[] = marktwerte.map(m => ({
    datum: m.geschaetzt_am,
    marktwert: m.marktwert_brutto,
  }));

  const szenarien = berechneSzenarien(afaInput, neusterMarktwert, heuteDatum, marktwertPunkte);
  const banner = berechneBannerZustand(afaInput, neusterMarktwert, heuteDatum);

  // Restbuchwert heute
  const rbwHeute = berechneRestbuchwert(afaInput, heuteDatum);

  // Diagramm-Daten
  const chartData = buildChartData(afaVerlauf, marktwertPunkte);

  return (
    <div className="space-y-4">

      {/* ── Banner A ──────────────────────────────────────────────────────── */}
      {banner.bannerA && (
        <div
          data-testid="banner-a"
          className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3"
        >
          <TrendingDown className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            <span className="font-semibold">Jetzt verkaufen bringt höchsten Buchverlust.</span>{' '}
            In weniger als 6 Monaten fällt der Restbuchwert unter 30 % des aktuellen Marktwerts —
            ein Buchverlust ist steuerlich am günstigsten.
          </p>
        </div>
      )}

      {/* ── Banner B ──────────────────────────────────────────────────────── */}
      {banner.bannerB && (
        <div
          data-testid="banner-b"
          className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            <span className="font-semibold">⚠ Vollabschreibung naht — VOR diesem Zeitpunkt verkaufen!</span>{' '}
            Ab {formatDateDE(vollabschreibungsDatum)} ist das Fahrzeug vollständig abgeschrieben.
            Ein Verkauf danach erzeugt einen voll steuerpflichtigen Buchgewinn.
          </p>
        </div>
      )}

      {/* ── AfA-Verlauf-Diagramm ──────────────────────────────────────────── */}
      <div
        data-testid="afa-diagramm"
        className="bg-white rounded-xl border border-gray-100 p-4"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          AfA-Verlauf
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
              width={36}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatEuro(value),
                name === 'restbuchwert' ? 'Restbuchwert' : 'Marktwert',
              ]}
              labelFormatter={l => l}
            />
            <Legend
              formatter={(value) => value === 'restbuchwert' ? 'Restbuchwert (AfA)' : 'Marktwert'}
              wrapperStyle={{ fontSize: 11 }}
            />

            {/* Heute-Marker (grün) */}
            <ReferenceLine
              x={formatMonthDE(heuteDatum)}
              stroke="#10b981"
              strokeDasharray="4 2"
              label={{ value: 'Heute', position: 'insideTopRight', fontSize: 9, fill: '#10b981' }}
            />

            {/* Vollabschreibungs-Marker (rot) */}
            <ReferenceLine
              x={formatMonthDE(vollabschreibungsDatum)}
              stroke="#ef4444"
              strokeDasharray="4 2"
              label={{ value: 'Vollabschr.!', position: 'insideTopLeft', fontSize: 9, fill: '#ef4444' }}
            />

            {/* Schnittpunkt-Marker (lila) */}
            {szenarien.optimal && (
              <ReferenceLine
                x={formatMonthDE(szenarien.optimal.datum)}
                stroke="#6366f1"
                strokeDasharray="4 2"
                label={{ value: 'Optimal', position: 'insideTopRight', fontSize: 9, fill: '#6366f1' }}
              />
            )}

            <Line
              type="monotone"
              dataKey="restbuchwert"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            {marktwertPunkte.length > 0 && (
              <Line
                type="monotone"
                dataKey="marktwert"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3, fill: '#3b82f6' }}
                connectNulls={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-2 flex items-center gap-4 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-0.5 bg-red-500" /> Restbuchwert (AfA)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-0.5 bg-blue-500" /> Marktwert
          </span>
          {szenarien.optimal && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-4 h-0.5 bg-indigo-500" style={{ borderTop: '2px dashed #6366f1', background: 'none' }} />
              Schnittpunkt = Optimal
            </span>
          )}
        </div>
      </div>

      {/* ── Empfehlungs-Card (3 Szenarien) ───────────────────────────────── */}
      <div
        data-testid="empfehlungs-card"
        className="space-y-3"
      >
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Szenarien
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SzenarioCard
            title="Jetzt verkaufen"
            datum={szenarien.jetzt.datum}
            restbuchwert={szenarien.jetzt.restbuchwert}
            marktwert={szenarien.jetzt.marktwert}
            buchgewinn={szenarien.jetzt.buchgewinn}
          />

          {szenarien.optimal ? (
            <SzenarioCard
              title="Optimaler Zeitpunkt"
              datum={szenarien.optimal.datum}
              restbuchwert={szenarien.optimal.restbuchwert}
              marktwert={szenarien.optimal.marktwert}
              buchgewinn={szenarien.optimal.buchgewinn}
              highlighted
            />
          ) : (
            <div
              className="rounded-xl border border-gray-100 bg-white p-4 flex flex-col items-center justify-center text-center"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                Optimaler Zeitpunkt
              </div>
              <p className="text-xs text-gray-400">
                {neusterMarktwert
                  ? 'Kein Schnittpunkt berechenbar — Marktwert liegt stets über Restbuchwert.'
                  : 'Marktwert erfassen, um optimalen Zeitpunkt zu berechnen.'}
              </p>
            </div>
          )}

          <SzenarioCard
            title="⚠ Bei Vollabschreibung"
            datum={szenarien.vollabschreibung.datum}
            restbuchwert={0}
            marktwert={szenarien.vollabschreibung.marktwert}
            buchgewinn={szenarien.vollabschreibung.buchgewinn}
            warning
          />
        </div>

        {/* Aktueller Restbuchwert als Kurzinfo */}
        <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between text-xs text-gray-500">
          <span>Restbuchwert heute ({formatDateDE(heuteDatum)})</span>
          <span className="font-semibold text-gray-800">{formatEuro(rbwHeute)}</span>
        </div>
      </div>

      {/* ── Manueller Marktwert-Eintrag ───────────────────────────────────── */}
      <div
        data-testid="marktwert-eingabe"
        className="bg-white rounded-xl border border-gray-100 p-4 space-y-3"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Marktwert manuell erfassen
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={marktwertInput}
            onChange={e => setMarktwertInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveMarktwert(); }}
            placeholder="z.B. 25000"
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/30 focus:border-[#C41E3A]"
          />
          <button
            onClick={handleSaveMarktwert}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#C41E3A' }}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Speichern
          </button>
        </div>
        <p className="text-[10px] text-gray-400">
          Marktwert-Schätzung (brutto) für heute eintragen. Quelle: manuell.
          Automatischer Abruf kommt in einer späteren Version.
        </p>

        {/* Letzte Einträge */}
        {marktwerte.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Letzte Einträge
            </div>
            {marktwerte.slice(0, 3).map(m => (
              <div key={m.id} className="flex justify-between text-xs text-gray-500 py-1 border-b border-gray-50 last:border-0">
                <span>{formatDateDE(m.geschaetzt_am)}</span>
                <span className="font-medium text-gray-700">{formatEuro(m.marktwert_brutto)}</span>
                <span className="text-gray-400">{m.quelle ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
